import { Injectable } from '@angular/core';
import { CacheService } from './cache.service';
import { ApiService } from './api.service';
import { BehaviorSubject, firstValueFrom, map, Observable, of, tap } from 'rxjs';
import { OemSettings } from '@interfaces/settings.interfaces';
import { RpcApiResponse } from '@interfaces/api.interfaces';
import { OEM, Tenant } from '@interfaces/user.interfaces';

@Injectable({
    providedIn: 'root',
})
export class SettingsService {
    private oemCode?: BehaviorSubject<string> = new BehaviorSubject(null);

    constructor(
        private readonly cacheService: CacheService,
        private readonly apiService: ApiService
    ) {}

    public getOemSettings(oemCode?: string): Promise<OemSettings> {
        //In the case of a customer, we need to get the oemCode from the url
        if (!oemCode) {
            oemCode = this.getOemCode();
        }
        const cacheKey = `oem-settings-${oemCode}`;
        if (this.cacheService.has(cacheKey)) {
            return firstValueFrom(of(this.cacheService.get(cacheKey)));
        }

        return firstValueFrom(
            this.apiService.getV2$('api/dealer/oemSettings?oemCode=' + oemCode, null, false).pipe(
                tap((response: RpcApiResponse<OemSettings>) => {
                    if (response.data && response.data.oemCode) {
                        this.cacheService.set(cacheKey, response.data);
                    }
                }),
                map((response: RpcApiResponse<OemSettings>) => {
                    return response.data;
                })
            )
        );
    }

    public setOemCode(oemCode: string) {
        this.oemCode.next(oemCode);
    }

    public oemCodeEvent(): Observable<string> {
        return this.oemCode.asObservable();
    }

    //Note: This is only used in the case of a customer, which does not have an associated oemCode
    //In the future, we may want to move oem code to a deployment level variable
    private getOemCode(): string {
        if (this.oemCode.value) {
            return this.oemCode.value;
        }

        if (window.location.href.includes(Tenant.stellantis) || window.location.href.includes(Tenant.stellantisEZDOCS)) {
            return OEM.stellantis;
        } else {
            return OEM.default;
        }
    }
}
